<!-- eslint-disable vue/no-mutating-props -->
<template>
    <hox-modal is-on-top-of-everything @close="close">
        <template #header>Add Asset from Link</template>
        <div class="media-library-add-asset-from-url">
            <div class="media-library-add-asset-from-url__input-wrapper">
                <Input
                    ref="urlInput"
                    :model-value="assetUrl"
                    placeholder="E.g. https://cdn.hoxton.co/assets/logo.png"
                    prefix="ios-link"
                    data-testid="add-asset-url-modal__input"
                    @onChange="onAssetUrlChange"
                />
            </div>
            <asset-preview
                data-testid="add-asset-url-modal__preview-image"
                :asset-url="assetUrl"
                @error="assetLoadError = true"
                @load="onAssetLoaded"
            >
                <template #errorMessage>
                    <hox-alert type="danger">
                        <template #title>
                            <p>There was an error when trying to display the link as an asset</p>
                        </template>
                        <template #content>
                            <p>
                                This may be because it does not exist, is not an asset or is missing the
                                <code>https://</code>
                                prefix.
                            </p>
                            <p>
                                If you believe that the link is correct then the next step to take is to manually check
                                if it exists in your browser.
                            </p>
                        </template>
                        <template #actionItems>
                            <Button ghost target="_blank" :to="assetUrl" type="error">Manually check link</Button>
                        </template>
                    </hox-alert>
                </template>
            </asset-preview>
        </div>
        <template #footer>
            <div>
                <Button
                    class="media-library-add-asset-from-url__button"
                    data-testid="add-asset-url-modal__cancel-button"
                    @click="close"
                >
                    Cancel
                </Button>
                <Button
                    class="media-library-add-asset-from-url__button"
                    data-testid="add-asset-url-modal__add-button"
                    :disabled="!assetUrl || !assetLoaded || assetLoadError"
                    type="primary"
                    @click="addImageToLibrary"
                >
                    Add Asset To Library
                </Button>
            </div>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import updateMediaFilesMutation from "@/apollo/mutations/UpdateMediaFiles.gql";
import uploadMediaFilesMutation from "@/apollo/mutations/UploadMediaFiles.gql";
import bus from "@/bus";

import HoxAlert from "@/components/common/Alert.vue";
import AssetPreview from "@/components/common/AssetPreview.vue";
import HoxModal from "@/components/Modal/Modal/Modal.vue";

import { debounce, getFileNameFromUrl, splitFileNameAndExtension } from "@/utils";

const MediaLibraryAddAssetFromUrlModal = defineComponent({
    components: {
        HoxAlert,
        HoxModal,
        AssetPreview
    },
    props: {
        folderId: {
            type: String,
            required: true
        },
        mediaItems: {
            required: true,
            type: Array as PropType<{ id: string; type: string; name: string }[]>
        },
        externalAssetUrl: {
            type: String,
            default: ""
        }
    },

    emits: ["assetAdded", "close"],

    data() {
        return {
            assetUrl: this.externalAssetUrl,
            isAddingAssetToLibrary: false,
            assetLoaded: false,
            assetLoadError: false
        };
    },
    computed: {
        clientId() {
            return this.$route.params.clientId;
        }
    },

    async mounted() {
        await this.$nextTick();
        (this.$refs.urlInput as HTMLInputElement).focus();
    },

    methods: {
        close() {
            this.$emit("close");
        },

        onAssetUrlChange: debounce(function handler(this: typeof MediaLibraryAddAssetFromUrlModal, event: Event) {
            this.assetUrl = (event.target as HTMLInputElement).value;
        }, 300),

        onAssetLoaded() {
            this.assetLoaded = true;
            this.assetLoadError = false;
        },

        async addImageToLibrary() {
            if (this.isAddingAssetToLibrary) {
                return;
            }

            this.isAddingAssetToLibrary = true;
            bus.$emit("percentLoadEvent", 50);

            const fileName = getFileNameFromUrl(this.assetUrl);
            let { name: fileNameWithoutExtension } = splitFileNameAndExtension(getFileNameFromUrl(this.assetUrl));
            if (fileNameWithoutExtension === "0") {
                const timestamp = Date.now();
                const randomSuffix = Math.random().toString(36).substring(2, 8);
                fileNameWithoutExtension = `${timestamp}_${randomSuffix}`;
            }
            const existingMediaItem = this.mediaItems.find(item => item.type === "FILE" && item.name === fileName);
            let createdOrUpdatedfileId;

            try {
                if (existingMediaItem) {
                    const res = await this.$apollo.mutate({
                        mutation: updateMediaFilesMutation,
                        variables: {
                            clientId: this.clientId,
                            files: [
                                {
                                    fileId: existingMediaItem.id,
                                    name: fileNameWithoutExtension,
                                    url: this.assetUrl
                                }
                            ],
                            folderId: this.folderId
                        }
                    });
                    createdOrUpdatedfileId = res.data.updateMediaFiles[0].id;
                } else {
                    const res = await this.$apollo.mutate({
                        mutation: uploadMediaFilesMutation,
                        variables: {
                            clientId: this.clientId,
                            files: [
                                {
                                    name: fileNameWithoutExtension,
                                    url: this.assetUrl
                                }
                            ],
                            folderId: this.folderId
                        }
                    });
                    createdOrUpdatedfileId = res.data.uploadMediaFiles[0].id;
                }
            } catch {
                const errorMessage = existingMediaItem
                    ? "There was an unexpected error while trying to update the asset"
                    : "There was an unexpected error while attempting to add the asset to the Content Library";

                this.$snackbar.error(errorMessage);
                return;
            } finally {
                this.isAddingAssetToLibrary = false;
                bus.$emit("percentLoadEvent", 100);
            }
            this.$emit("assetAdded", createdOrUpdatedfileId);
        }
    }
});

export default MediaLibraryAddAssetFromUrlModal;
</script>

<style lang="scss">
@use "@style/variables" as *;

$check-color: $grey3;

.media-library-add-asset-from-url {
    display: flex;
    flex-direction: column;
    height: 50vh;
    justify-content: flex-start;
    align-content: center;
}

.media-library-add-asset-from-url__button {
    margin: 0 0 0 $spacing-small;
}

.media-library-add-asset-from-url__input-wrapper {
    margin: 0 0 $spacing;
}
</style>
